import React, { useEffect } from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { AppBar, CardContent, CircularProgress, Link, Toolbar, Typography } from '@mui/material';
import { PageContainer } from '../components/PageContainer';
import { SignalRDataProvider } from '../data/DataProviders/SignalRDataProvider';
import Settings from './settings/Settings';
import Legal from './legal/Legal';
import { AppRoutes } from '../routes';
import { Overview } from './overview/Overview';
import { Timeline } from './timelines/Timeline';
import { Devices } from './devices/Devices';
import { UserManagement } from './user-management/UserManagement';
import { ToastProvider } from '../data/DataProviders/ToastProvider';
import { LocationManagement } from './location-management/LocationManagement';
import { isRecordFound, RecordDataState, useSelectedOrganizationData } from '../hooks/data-hooks';
import { LocationListDataProvider } from '../data/DataProviders/LocationListDataProvider';
import { ContentContainer } from '../components/ContentContainer';
import { RecordDataIndicators } from '../components/RecordDataIndicators';
import { useLanguageLocale } from '../hooks/language-locale-hooks';
import { getUserId, useAuthState } from '../components/AuthContextProvider';
import { DataProviderCache } from '../data/DataProviders/DataProviderCache';
import { GetOrgCheckProvider } from '../data/DataProviders/GetOrgCheckProvider';
import { ManageDeviceProfilesContainer } from '../components/ManageDeviceProfiles';
import { ManageAlarms } from '../components/ManageAlarms';
import { useOrganizationList } from '../data/DataProviders/OrganizationListProvider';
import { EmptyState } from '@brightlayer-ui/react-components';
import { ShowChart, } from '@mui/icons-material';
import { CONTACT_EMAIL } from '../constants';
import { OnlyLogout } from '../components/OnlyLogout/OnlyLogout';
import makeStyles from '@mui/styles/makeStyles';

// Defining custom styles using makeStyles
const useStyles = makeStyles({
    cardContent: {
        flex: '1 1 0px',
        overflow: 'auto',
    },
    centered: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh', // Full viewport height
    },
});

export const Content: React.FC = () => {
    const { t } = useLanguageLocale();
    const selectedOrganizationData = useSelectedOrganizationData();
    const authState = useAuthState();
    const userId = getUserId(authState) || '';
    const { organizationList } = useOrganizationList();
    const [orgList, setOrgList] = React.useState(organizationList);
    const classes = useStyles();

    // useEffect hook to update the organization list state when the organizationList changes
    useEffect(() => {
        setOrgList(organizationList);
    }, [organizationList]);

    // Check if the selected organization data is still loading
    if (selectedOrganizationData.state === RecordDataState.Loading) {
        return (
            <CardContent className={`${classes.cardContent} ${classes.centered}`}>
                <CircularProgress id={'progress-spinner'} />
            </CardContent>
        );
    }

    // Check if the organization list is blank and the selected organization data is not found
    if (orgList && !isRecordFound(selectedOrganizationData)) {
        return (
            <>
                <AppBar position="sticky" color="default" style={{ background: 'default', zIndex: 500 }}>
                    <Toolbar style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: 16 }}>
                        <OnlyLogout />
                    </Toolbar>
                </AppBar>
                <ContentContainer>
                    {orgList ? (
                        <EmptyState
                            icon={<ShowChart id="fail-icon" style={{ fontSize: '24px' }} />}
                            title={'No Organizations'}
                            actions={
                                <>
                                    <Typography variant="subtitle2" style={{ textAlign: 'center' }}>
                                        {'You don’t have organizations.'}
                                    </Typography>
                                    <Typography variant="subtitle2" style={{ textAlign: 'center' }}>
                                        {'Contact Eaton support at'}{' '}
                                        <Link underline="always" href={`mailto:${CONTACT_EMAIL}`}>
                                            {CONTACT_EMAIL}
                                        </Link>{' '}
                                    </Typography>
                                </>
                            }
                        />
                    ) : (
                        <RecordDataIndicators
                            recordData={selectedOrganizationData}
                            errorMessage={t('DASHBOARD.ERROR_LOADING_ORGANIZATION')}
                            notFoundIcon="Business"
                            notFoundMessage={t('DASHBOARD.ORGANIZATION_NOT_FOUND')}
                        />
                    )}
                </ContentContainer>
            </>
        );
    }
    if (isRecordFound(selectedOrganizationData)) {
        return (
            <GetOrgCheckProvider organizationId={selectedOrganizationData.value.id}>
                <ToastProvider>
                    <DataProviderCache for={'location-list'}>
                        <LocationListDataProvider
                            organizationId={selectedOrganizationData.value.id}
                            userId={userId}
                            operation="view"
                        >
                            <PageContainer>
                                <Switch>
                                    <Route path={['/overview', '/timelines', '/locations', '/devices']}>
                                        <SignalRDataProvider>
                                            <Switch>
                                                <Route path={AppRoutes.Overview} component={Overview} />
                                                <Route path={AppRoutes.Timeline} component={Timeline} />
                                                <Route path={AppRoutes.Devices} component={Devices} />
                                            </Switch>
                                        </SignalRDataProvider>
                                    </Route>
                                    <Route
                                        path={[
                                            '/settings',
                                            '/settings/myProfile',
                                            '/manage-user',
                                            '/manage-location',
                                            '/manage-device-profiles',
                                            '/manage-alarms',
                                        ]}
                                    >
                                        <SignalRDataProvider>
                                            <Switch>
                                                <Route path={AppRoutes.Settings} component={Settings} />
                                                <Route path={AppRoutes.MyProfile} component={Settings} />
                                                <Route path={AppRoutes.ManageUser} component={UserManagement} />
                                                <Route path={AppRoutes.ManageLocation} component={LocationManagement} />
                                                <Route
                                                    path={AppRoutes.ManageDeviceProfiles}
                                                    component={ManageDeviceProfilesContainer}
                                                />
                                                <Route path={AppRoutes.ManageAlarms} component={ManageAlarms} />
                                            </Switch>
                                        </SignalRDataProvider>
                                    </Route>

                                    <Route path="/legal">
                                        <Legal />
                                    </Route>

                                    <Redirect to="/overview" />
                                </Switch>
                            </PageContainer>
                        </LocationListDataProvider>
                    </DataProviderCache>
                </ToastProvider>
            </GetOrgCheckProvider>
        );
    }
    return null;
};